import { config } from '../config';

export interface ClientSupport {
  minSupportedVersion: string;
  recommendedVersion: string | null;
  blockedVersions: string[];
  version: string | null;
  supported: boolean;
  blocked: boolean;
  message: string | null;
}

/**
 * Asks the API whether the build we are running is still one it supports.
 *
 * Members can roll back to an older release from the download archive, and the
 * API only promises compatibility for a limited window. This is how a build
 * that has fallen outside that window finds out, instead of failing in ways
 * that look like unrelated bugs.
 *
 * Called with a plain fetch rather than the generated client because the
 * endpoint is public and needs to work even when the session is in a bad state.
 */
class ClientSupportService {
  static async check(
    version: string = config.VERSION,
  ): Promise<ClientSupport | null> {
    const url = `${config.API_BASE_URL}/client-support?version=${encodeURIComponent(version)}`;

    try {
      const response = await fetch(url, {
        headers: { 'X-App-Version': version },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      return (await response.json()) as ClientSupport;
    } catch (error) {
      // An unreachable or older API must not produce a scary banner, so treat
      // a failed check as "no opinion" and stay quiet.
      console.error('Could not check client support status:', error);
      return null;
    }
  }
}

export default ClientSupportService;
